export interface PracticeSessionTitleItem {
  title?: string | null;
  durationSeconds?: number | null;
  duration_seconds?: number | null;
}

export const MAX_PRACTICE_SESSION_SECONDS = 12 * 60 * 60;
export const MAX_PRACTICE_SESSION_MINUTES = MAX_PRACTICE_SESSION_SECONDS / 60;

interface CreatePracticeSessionTitleOptions {
  fallbackTitle: string;
  moreItemsLabel: (count: number) => string;
}

function getItemDurationSeconds(item: PracticeSessionTitleItem) {
  const value = item.durationSeconds ?? item.duration_seconds ?? 0;
  return Number.isFinite(value) ? Number(value) : 0;
}

export function normalizePracticeElapsedSeconds(seconds: number | null | undefined) {
  const value = Number(seconds ?? 0);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(Math.round(value), MAX_PRACTICE_SESSION_SECONDS);
}

export function getPracticeDurationMinutes(seconds: number | null | undefined) {
  const safeSeconds = normalizePracticeElapsedSeconds(seconds);
  if (safeSeconds <= 0) return null;
  return Math.max(1, Math.round(safeSeconds / 60));
}

export function normalizePracticeDurationMinutes(minutes: number | null | undefined) {
  const value = Number(minutes ?? 0);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(Math.round(value), MAX_PRACTICE_SESSION_MINUTES);
}

export function isPracticeDurationOutlier(minutes: number | null | undefined) {
  const value = Number(minutes ?? 0);
  return Number.isFinite(value) && value > MAX_PRACTICE_SESSION_MINUTES;
}

export function formatPracticeDurationMinutes(
  minutes: number | null | undefined,
  labels: { minutes: string; hours: string } = { minutes: "min", hours: "h" },
) {
  const safeMinutes = normalizePracticeDurationMinutes(minutes);
  if (safeMinutes <= 0) return `0 ${labels.minutes}`;

  const prefix = isPracticeDurationOutlier(minutes) ? "> " : "";
  if (safeMinutes < 60) return `${prefix}${safeMinutes} ${labels.minutes}`;

  const hours = Math.floor(safeMinutes / 60);
  const restMinutes = safeMinutes % 60;
  if (restMinutes === 0) return `${prefix}${hours} ${labels.hours}`;
  return `${prefix}${hours} ${labels.hours} ${restMinutes} ${labels.minutes}`;
}

export function getPracticeWeekSummary<T extends { minutes: number | null | undefined }>(days: T[]) {
  const normalizedDays = days.map((day) => ({
    day,
    minutes: normalizePracticeDurationMinutes(day.minutes),
  }));
  const activeDays = normalizedDays.filter(({ minutes }) => minutes > 0);
  const weekMinutes = normalizedDays.reduce((sum, { minutes }) => sum + minutes, 0);
  const bestDay = activeDays.reduce<T | null>((best, current) => {
    if (!best) return current.day;
    return normalizePracticeDurationMinutes(current.day.minutes) > normalizePracticeDurationMinutes(best.minutes)
      ? current.day
      : best;
  }, null);

  return {
    activeDays: activeDays.length,
    averageActiveDayMinutes: activeDays.length > 0 ? Math.round(weekMinutes / activeDays.length) : 0,
    bestDay,
    weekMinutes,
  };
}

export function createPracticeSessionTitle(
  items: PracticeSessionTitleItem[],
  options: CreatePracticeSessionTitleOptions,
) {
  const candidates = items
    .map((item, index) => ({
      index,
      title: item.title?.trim() ?? "",
      durationSeconds: getItemDurationSeconds(item),
    }))
    .filter((item) => item.title.length > 0);

  if (candidates.length === 0) return options.fallbackTitle.trim() || "Session";

  const [primary] = [...candidates].sort((a, b) => {
    if (b.durationSeconds !== a.durationSeconds) return b.durationSeconds - a.durationSeconds;
    return a.index - b.index;
  });
  const additionalCount = candidates.length - 1;

  if (additionalCount <= 0) return primary.title;
  return `${primary.title} + ${options.moreItemsLabel(additionalCount)}`;
}

export function formatSessionMoreItems(count: number, singular: string, pluralTemplate: string) {
  if (count === 1) return singular;
  return pluralTemplate.replace("{count}", String(count));
}

export function getSessionStartedAt<T extends { started_at?: string | null; completed_at: string }>(session: T) {
  return session.started_at ?? session.completed_at;
}

export function getSessionEndedAt<T extends { ended_at?: string | null; completed_at: string }>(session: T) {
  return session.ended_at ?? session.completed_at;
}

export function getFallbackStartedAt(endedAt: string, elapsedSeconds: number) {
  const endedAtMs = Date.parse(endedAt);
  if (!Number.isFinite(endedAtMs)) return endedAt;
  return new Date(endedAtMs - normalizePracticeElapsedSeconds(elapsedSeconds) * 1000).toISOString();
}

export function getSafeSessionStartedAt(
  endedAt: string,
  startedAt: string | null | undefined,
  elapsedSeconds: number | null | undefined,
) {
  const endedAtMs = Date.parse(endedAt);
  const startedAtMs = startedAt ? Date.parse(startedAt) : Number.NaN;
  if (Number.isFinite(endedAtMs) && Number.isFinite(startedAtMs)) {
    const deltaSeconds = Math.round((endedAtMs - startedAtMs) / 1000);
    if (deltaSeconds >= 0 && deltaSeconds <= MAX_PRACTICE_SESSION_SECONDS) {
      return startedAt!;
    }
  }

  return getFallbackStartedAt(endedAt, normalizePracticeElapsedSeconds(elapsedSeconds));
}
